import React, { useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "../../api/apiClient";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import { PageContainer } from "../../components/layout/PageContainer";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import PredefinedRepairForm from "./PredefinedRepairForm";
import { Spinner } from "@/components/ui/spinner";

const EditPredefinedRepair = () => {
  const permissions = useSelector((state) => state.userpermission_data);
  const hasPermission = useCallback(
    (permission) => {
      return permissions && permissions.includes(permission);
    },
    [permissions],
  );
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();


  useEffect(() => {
    if (!hasPermission("predefined repair job update")) {
      toast.error("Unauthorized", {
        description:
          "You do not have permission to access this page. Redirecting...",
      });
      setTimeout(() => {
        navigate(all_routes.dashboard);
      }, 3000);
    }
  }, [hasPermission, navigate]);

  const { data: template, isLoading } = useQuery({
    queryKey: ["predefined-repair", id],
    queryFn: async () => {
      const res = await apiClient.get(
        `/predefined-repair-jobs/${id}`,
        {
          params: {
            with: [
              "parts_n_components.product",
              "labor_n_timetracking.employee",
              "repair_categories",
            ],
          },
        },
      );
      return res.data.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updatedData) => {
      const response = await apiClient.put(
        `/predefined-repair-jobs/${id}`,
        updatedData,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predefined-repairs"] });
      toast.success("Template updated successfully");
      navigate(all_routes.predefinedRepairs);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to update template");
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader className="mb-6">
        <PageHeaderHeading>Edit Template: {template?.name}</PageHeaderHeading>
      </PageHeader>
      <PredefinedRepairForm
        initialData={template}
        onSubmit={(data) => updateMutation.mutate(data)}
        isSubmitting={updateMutation.isPending}
        onCancel={() => navigate(all_routes.predefinedRepairs)}
      />
    </PageContainer>
  );
};

export default EditPredefinedRepair;



